import { useEffect, useCallback, useState, useReducer } from "react";
import { useLocation } from "react-router-dom";

import { useAuthContext } from "context/AuthContext";
import { useDocument } from "hooks/useDocument";

import { useAbac } from "react-abac";
import { Permission } from "models/abac";

import initialValues from "pages/subjmath/manage/schemas/initialValues";
import validations, {
  noValidation,
} from "pages/subjmath/manage/schemas/validations";

import { sendMail } from "services/callable";

import { useCollection } from "hooks/useCollection";

const collectionPath = "subjmath";

const usersQueries = {
  whereQueries: [
    {
      field: "deletedAt",
      condition: "==",
      value: null,
    },
  ],
};

const initialState = {
  data: initialValues,
  isPending: false,
  error: null,
  success: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "DISMISS":
      return {
        isPending: false,
        data: initialValues,
        success: null,
        error: null,
      };
    case "IS_PENDING":
      return {
        isPending: true,
        data: initialValues,
        success: null,
        error: null,
      };
    case "INITIAL_SUBJMATH":
      return {
        isPending: false,
        data: action.payload,
        success: null,
        error: null,
      };
    case "RETRIEVED_SUBJMATH":
      return {
        isPending: false,
        data: action.payload,
        success: null,
        error: null,
      };
    case "REJECTED_SUBJMATH":
      return {
        isPending: false,
        data: action.payload,
        success: `Successfully rejected the subjmath, ${action.payload.subjmathName}.`,
        error: null,
      };
    case "CREATED_SUBJMATH":
      return {
        isPending: false,
        data: action.payload,
        success: `Successfully created the subjmath, ${action.payload.subjmathName}.`,
        error: null,
      };
    case "UPDATED_SUBJMATH":
      return {
        isPending: false,
        data: action.payload,
        success: `Successfully updated the subjmath, ${action.payload.subjmathName}.`,
        error: null,
      };
    case "DELETED_SUBJMATH":
      return {
        isPending: false,
        data: action.payload,
        success: `Successfully deleted the subjmath, ${action.payload.subjmathName}.`,
        error: null,
      };
    case "ERROR":
      return {
        isPending: false,
        data: initialValues,
        success: null,
        error: action.error,
      };
    default:
      return state;
  }
};

export const useMathManager = (mode, subjmathId) => {
  const [response, dispatch] = useReducer(reducer, initialState);
  const [isUnmounted, setIsUnmounted] = useState(false);

  const { collectionData: usersData } = useCollection("users", usersQueries);

  const { user } = useAuthContext();
  const { createDoc, retrieveDoc, updateDoc, deleteDoc, serverTimestamp } =
    useDocument();

  const { pathname } = useLocation();

  const { userHasPermissions } = useAbac();

  const dispatchIfNotUnmounted = useCallback(
    (action) => {
      if (!isUnmounted) {
        dispatch(action);
      }
    },
    [isUnmounted]
  );

  const dispatchDismiss = useCallback(
    () => dispatchIfNotUnmounted({ type: "DISMISS" }),
    [dispatchIfNotUnmounted]
  );

  const dispatchError = useCallback(
    (err) => {
      console.error(err);
      if (
        ![
          "PermissionDeniedError",
          "OperationInvalidError",
          "SubjMathInvalidError",
        ].includes(err.name)
      ) {
        err.message = "The operation couldn't be completed";
        err.name = "OperationIncompleteError";
        // TODO: send error stack to server
      }
      dispatchIfNotUnmounted({
        type: "ERROR",
        error: err,
      });
    },
    [dispatchIfNotUnmounted]
  );

  const validateOperation = useCallback(async () => {
    try {
      dispatchIfNotUnmounted({ type: "IS_PENDING" });
      let operationInvalidError = new Error(
        "Invalid Operation. You are not allowed to carry out this activity."
      );
      operationInvalidError.name = "OperationInvalidError";

      const retrievedSubjMath =
        subjmathId && (await retrieveDoc(collectionPath, subjmathId));

      // validate operation
      switch (mode) {
        case "new":
          if (!pathname.includes("/subjmath/manage")) {
            throw operationInvalidError;
          }

          dispatchIfNotUnmounted({
            type: "INITIAL_SUBJMATH",
            payload: initialValues,
          });
          break;
        case "view":
        case "edit":
        case "delete":
          if (!pathname.includes("/subjmath/manage")) {
            throw operationInvalidError;
          }

          dispatchIfNotUnmounted({
            type: "RETRIEVED_SUBJMATH",
            payload: retrievedSubjMath.data,
          });
          break;
        default:
          throw operationInvalidError;
      }
    } catch (err) {
      dispatchError(err);
    }
  }, [
    dispatchIfNotUnmounted,
    subjmathId,
    retrieveDoc,
    mode,
    pathname,
    dispatchError,
  ]);

  useEffect(() => {
    try {
      validateOperation();
    } catch (err) {
      dispatchError(err);
    }
    return () => {
      setIsUnmounted(true);
    };
  }, [dispatchError, validateOperation]);

  let modeTitle = "";
  let modeSubmit = "";
  let modeFieldDisabled = true;
  let modePermission = "";
  let modeValidation = noValidation;
  switch (mode) {
    case "new":
      modeTitle = "Math Assignment";
      modeSubmit = "Create";
      modeFieldDisabled = false;
      modePermission = Permission.CREATE_SUBJMATH;
      modeValidation = validations;
      break;
    case "edit":
      modeTitle = "Edit SubjMath";
      modeSubmit = "Update";
      modeFieldDisabled = false;
      modePermission = Permission.UPDATE_SUBJMATH;
      modeValidation = validations;
      break;
    case "view":
      modeTitle = "View SubjMath";
      modeSubmit = "Close";
      modeFieldDisabled = true;
      modePermission = Permission.READ_SUBJMATH;
      modeValidation = noValidation;
      break;
    case "delete":
      modeTitle = "Delete SubjMath";
      modeSubmit = "Delete";
      modeFieldDisabled = true;
      modePermission = Permission.DELETE_SUBJMATH;
      modeValidation = noValidation;
      break;
    default:
      modeTitle = "Illegal Action";
  }

  // TODO: move to Service Layer custom hook
  const submitReject = async (values) => {
    try {
      dispatchIfNotUnmounted({ type: "IS_PENDING" });
      if (userHasPermissions(Permission.REJECT_SUBJMATH)) {
        const updatedDoc = await updateDoc(collectionPath, subjmathId, {
          approvedAt: null,
          approvedBy: null,
          rejectedAt: serverTimestamp(),
          rejectedBy: user.uid,
        });

        dispatchIfNotUnmounted({
          type: "REJECTED_SUBJMATH",
          payload: updatedDoc.data,
        });
      } else {
        let error = new Error(
          "Permission Denied. You are not allowed to reject subjmath."
        );
        error.name = "PermissionDeniedError";
        throw error;
      }
    } catch (err) {
      dispatchError(err);
    }
  };

  // TODO: move to Service Layer custom hook
  const submitNew = async (values) => {
    try {
      dispatchIfNotUnmounted({ type: "IS_PENDING" });
      if (userHasPermissions(Permission.CREATE_SUBJMATH)) {
        const createdDoc = await createDoc(
          collectionPath,
          {
            ...values,
            approvedAt: null,
            approvedBy: null,
            rejectedAt: null,
            rejectedBy: null,
          },
          user.uid
        );

        // Notification send to SUPERADMIN
        const notifiedUsers = usersData.filter((userData) => {
          return userData?.data?.roles?.some((element) =>
            ["SUPERADMIN"].includes(element)
          );
        });
        const toEmails = notifiedUsers.map(
          (notifiedUser) => notifiedUser.email
        );
        toEmails &&
          (await sendMail({
            fromName: "Goodair System",
            fromEmail: "crm@goodair.com.sg",
            toEmails: toEmails,
            emailSubject: `NEW SUBJMATH, ${values.subjmathName} (PENDING APPROVAL)`,
            emailContent: `This is an auto-generated message, please do not reply.`,
          }));

        dispatchIfNotUnmounted({
          type: "CREATED_SUBJMATH",
          payload: createdDoc.data,
        });
      } else {
        let error = new Error(
          "Permission Denied. You are not allowed to create subjmath."
        );
        error.name = "PermissionDeniedError";
        throw error;
      }
    } catch (err) {
      dispatchError(err);
    }
  };

  // TODO: move to Service Layer custom hook
  const submitEdit = async (values) => {
    try {
      dispatchIfNotUnmounted({ type: "IS_PENDING" });
      if (userHasPermissions(Permission.UPDATE_SUBJMATH)) {
        const updatedDoc = await updateDoc(collectionPath, subjmathId, {
          ...values,
          modifiedAt: serverTimestamp(),
          modifiedBy: user.uid,
          approvedAt: null,
          approvedBy: null,
        });

        // Notification send to SUPERADMIN
        const notifiedUsers = usersData.filter((userData) => {
          return userData?.data?.roles?.some((element) =>
            ["SUPERADMIN"].includes(element)
          );
        });
        const toEmails = notifiedUsers.map(
          (notifiedUser) => notifiedUser.email
        );
        toEmails &&
          (await sendMail({
            fromName: "Goodair System",
            fromEmail: "crm@goodair.com.sg",
            toEmails: toEmails,
            emailSubject: `NEW SUBJMATH, ${values.subjmathName} (PENDING APPROVAL)`,
            emailContent: `This is an auto-generated message, please do not reply.`,
          }));

        dispatchIfNotUnmounted({
          type: "UPDATED_SUBJMATH",
          payload: updatedDoc.data,
        });
      } else {
        let error = new Error(
          "Permission Denied. You are not allowed to update subjmath."
        );
        error.name = "PermissionDeniedError";
        throw error;
      }
    } catch (err) {
      dispatchError(err);
    }
  };

  // TODO: move to Service Layer custom hook
  const submitDelete = async () => {
    try {
      dispatchIfNotUnmounted({ type: "IS_PENDING" });
      if (userHasPermissions(Permission.DELETE_SUBJMATH)) {
        const deletedDoc = await deleteDoc(collectionPath, subjmathId, {
          deletedAt: serverTimestamp(),
          deletedBy: user.uid,
        });
        dispatchIfNotUnmounted({
          type: "DELETED_SUBJMATH",
          payload: deletedDoc.data,
        });
      } else {
        let error = new Error(
          "Permission Denied. You are not allowed to delete subjmath."
        );
        error.name = "PermissionDeniedError";
        throw error;
      }
    } catch (err) {
      dispatchError(err);
    }
  };

  return {
    modeTitle,
    modeSubmit,
    modeFieldDisabled,
    modePermission,
    modeValidation,
    submitReject,
    submitNew,
    submitEdit,
    submitDelete,
    response,
    dispatchDismiss,
    dispatchError,
  };
};
